import { useI18n } from 'gunsi-auth'

const translations = {
  en: {
    home: {
      tagline: 'Split bills with friends. Settle up easily.',
      cta: 'Get started',
    },
    nav: {
      groups: 'Groups',
      people: 'People',
      activity: 'Activity',
      converter: 'Converter',
      settings: 'Settings',
    },
    people: {
      title: 'People',
      empty: 'No one to settle with yet. Add someone to a group.',
      allSettled: 'Everyone is settled up',
      totalOwe: 'you owe',
      totalOwed: 'you get back',
      youOwe: 'you owe',
      owesYou: 'owes you',
      byGroup: 'Across groups',
      netOf: 'net across {n} groups — tap for details',
      settledSection: 'Settled up',
      ghostHint: 'Not linked to a Gunsi account: same-name entries in different groups are counted as one person.',
    },
    converter: {
      title: 'Currency converter',
    },
    detail: {
      title: 'Expense details',
      addedBy: 'Added by {name} on {date} at {time}',
      addedOn: 'Added on {date} at {time}',
      editedOn: 'Edited on {date} at {time}',
      owes: 'owes',
      paidByMulti: 'Paid by',
    },
    groups: {
      title: 'Your groups',
      empty: 'No groups yet — create one to start splitting.',
      new: 'New group',
      open: 'Open',
      youOwe: 'you owe',
      youAreOwed: 'you get',
      settled: 'all settled',
      archived: 'Archived',
      archivedTag: 'archived',
    },
    group: {
      members: 'Members',
      expenses: 'Expenses',
      balances: 'Balances',
      activity: 'Activity',
      addExpense: 'Add expense',
      addPayment: 'Settle up',
      noExpenses: 'No expenses yet. Add the first one.',
      noBalances: 'Nothing to settle.',
      settlePlan: 'Settle up',
      archive: 'Archive group',
      unarchive: 'Reopen group',
      archived: 'This group is archived — it no longer counts towards your totals.',
      archiveHint: 'The trip is over: expenses stay here to look at, but the group drops out of your totals and out of People.',
      unarchiveHint: 'Bring it back into your totals and into People.',
      archiveConfirm: 'Archive this group? Nothing is deleted — it just stops counting towards your totals.',
      archiveConfirmOpen: 'There are still open balances here ({amounts}). Archive anyway? Nothing is deleted, but the group stops counting towards your totals.',
      simplifyOn: 'Shortest way to zero the group. Debts get redirected, so it only works if everyone pays their line together.',
      simplifyOff: 'Who actually owes whom, pair by pair. Pay this and your debt with that person is gone.',
      paid: 'paid',
      owesTo: 'owes',
      youPaid: 'you paid',
      paidByN: 'paid by {n} people',
      youBorrowed: 'you borrowed',
      evenShare: "you're even",
      notInvolved: 'not involved',
      youOwe: 'you owe {name}',
      ownsYou: '{name} owes you',
      perPerson: 'per person',
      delete: 'Delete group',
      deleteConfirm: 'Delete "{name}" and all its expenses?',
      edit: 'Edit group',
      tallyTitle: 'Tally',
      tallyToggle: 'Show my shares on Tally',
      tallyHint: 'Personal setting: each expense adds your share as a Tally transaction (category "Even").',
      tallyBackfillConfirm: 'This group has {n} earlier expenses not yet on Tally. Add them now?',
      tallyBackfillOk: 'Add them',
      tallyWipeConfirm: 'There are {n} transactions already on Tally for this group. Remove them too?',
      tallyWipeOk: 'Remove them',
      tallyWipeCancel: 'Keep them',
      tallyResync: 'Re-sync now',
      tallyResyncConfirm: 'Re-sync this group to your Tally? Missing entries are recreated and amounts realigned. Anything you edited by hand on Tally — name, category, amount, date — is left untouched.',
      tallyResyncDone: '{n} transactions synced.',
      currencyView: 'Currency view',
      viewSeparate: 'Separate currencies',
      viewAllIn: 'All in {cur}',
      ratesMissing: 'Some exchange rates are unavailable — totals may be incomplete.',
    },
    newGroup: {
      title: 'New group',
      name: 'Group name',
      namePh: 'e.g. Berlin trip, Flatmates',
      emoji: 'Emoji',
      members: 'Members',
      memberPh: 'Name',
      addMember: 'Add member',
      you: 'You',
      create: 'Create',
      cancel: 'Cancel',
      linked: 'Gunsi',
      unlink: 'Unlink Gunsi account',
      noAccessShort: 'no access',
      linkNoAccess: 'This user has no access to Even yet — ask the admin to enable it.',
      membersLocked: 'Only the group owner can change members.',
      memberHasEntries: "{names} already has expenses in this group, so they can't be removed. To link them to a Gunsi account, type their name in their row and pick them from the list — that keeps their history.",
    },
    expense: {
      title: 'Add expense',
      editTitle: 'Edit expense',
      description: 'Description',
      descPh: 'e.g. Dinner, Hotel, Groceries',
      amount: 'Amount',
      paidBy: 'Paid by',
      multiPayer: '+ Multiple payers',
      onePayer: 'Single payer',
      splitMode: 'Split',
      split: {
        equal: 'Equally',
        exact: 'By exact amounts',
        percent: 'By percentage',
        shares: 'By shares',
        adjust: 'By adjustment (personal extras)',
      },
      splitShort: {
        equal: 'Equal',
        exact: 'Amounts',
        percent: 'Percent',
        shares: 'Shares',
        adjust: 'Extras',
      },
      participants: 'Participants',
      date: 'Date',
      time: 'Time',
      notes: 'Notes (optional)',
      notesPh: 'Add a note…',
      hideTally: "Don't show on my Tally",
      deleteConfirm: 'Delete this expense?',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      total: 'Total',
      remaining: 'Remaining',
      amountInvalid: 'Enter a valid amount, e.g. 12.50 or 12,50',
      rateMissing: 'Exchange rate for this date unavailable — connect and try again',
      adjustHint: '{rest} left to split equally · {each} each',
      perPerson: 'per person',
    },
    settle: {
      title: 'Settle up',
      from: 'From',
      to: 'To',
      amount: 'Amount',
      date: 'Date',
      notes: 'Notes (optional)',
      deleteConfirm: 'Delete this payment?',
      save: 'Record payment',
      cancel: 'Cancel',
    },
    settings: {
      title: 'Settings',
      appearance: 'Appearance',
      theme: 'Theme',
      themeSystem: 'System',
      themeLight: 'Light',
      themeDark: 'Dark',
      style: 'Style',
      styleModern: 'Modern',
      styleClassic: 'Classic',
      language: 'Language',
      defaultCurrency: 'Default currency',
      dateFormat: 'Date format',
      dateFormats: {
        auto: 'Automatic (follows the language)',
        dmy: 'DD/MM/YYYY',
        mdy: 'MM/DD/YYYY',
        ymd: 'YYYY-MM-DD',
      },
      data: 'Data',
      exportJson: 'Export JSON',
      importJson: 'Import JSON',
      reset: 'Reset all data',
      resetConfirm: 'Delete ALL groups, expenses and settlements? This cannot be undone.',
      invalidJson: 'Invalid JSON file.',
      account: 'Account',
    },
    activity: {
      title: 'Activity',
      empty: 'Nothing to show yet.',
      expense: 'expense',
      payment: 'payment',
      added: 'added',
      updated: 'updated',
      paidFor: 'paid for',
      nPeople: '{n} people',
      youLent: 'you lent',
      youReceived: 'you received',
      youSettled: 'you settled',
    },
    common: {
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      confirm: 'Confirm',
      back: 'Back',
      loading: 'Loading…',
      error: 'Something went wrong.',
      retry: 'Retry',
      you: 'you',
      and: 'and',
      currency: 'Currency',
      searchCurrencyPh: 'Search currency…',
      searchEmojiPh: 'Search emoji…',
      ok: 'OK',
      today: 'Today',
      now: 'Now',
    },
  },
  it: {
    home: {
      tagline: 'Dividi spese con gli amici. Pareggia con un tap.',
      cta: 'Inizia',
    },
    nav: {
      groups: 'Gruppi',
      people: 'Persone',
      activity: 'Attività',
      converter: 'Converti',
      settings: 'Impostazioni',
    },
    people: {
      title: 'Persone',
      empty: 'Nessuno con cui fare i conti. Aggiungi qualcuno a un gruppo.',
      allSettled: 'Tutto pareggiato con tutti',
      totalOwe: 'devi',
      totalOwed: 'ti spettano',
      youOwe: 'devi',
      owesYou: 'ti deve',
      byGroup: 'Nei gruppi',
      netOf: 'netto di {n} gruppi — tocca per il dettaglio',
      settledSection: 'In pari',
      ghostHint: 'Non collegata a un account Gunsi: nomi uguali in gruppi diversi vengono contati come la stessa persona.',
    },
    converter: {
      title: 'Convertitore valute',
    },
    detail: {
      title: 'Dettagli spesa',
      addedBy: 'Aggiunta da {name} il {date} alle {time}',
      addedOn: 'Aggiunta il {date} alle {time}',
      editedOn: 'Modificata il {date} alle {time}',
      owes: 'deve',
      paidByMulti: 'Pagata da',
    },
    groups: {
      title: 'I tuoi gruppi',
      empty: 'Nessun gruppo — creane uno per iniziare.',
      new: 'Nuovo gruppo',
      open: 'Apri',
      youOwe: 'devi',
      youAreOwed: 'ti spettano',
      settled: 'tutto pareggiato',
      archived: 'Archiviati',
      archivedTag: 'archiviato',
    },
    group: {
      members: 'Membri',
      expenses: 'Spese',
      balances: 'Bilanci',
      activity: 'Attività',
      addExpense: 'Aggiungi spesa',
      addPayment: 'Pareggia',
      noExpenses: 'Nessuna spesa. Aggiungi la prima.',
      noBalances: 'Niente da pareggiare.',
      settlePlan: 'Chiusura',
      archive: 'Archivia gruppo',
      unarchive: 'Riapri gruppo',
      archived: 'Gruppo archiviato — non conta più nei tuoi totali.',
      archiveHint: 'Il viaggio è finito: le spese restano qui da consultare, ma il gruppo esce dai tuoi totali e dalla pagina Persone.',
      unarchiveHint: 'Rimettilo nei tuoi totali e nella pagina Persone.',
      archiveConfirm: 'Archiviare questo gruppo? Non si cancella niente: smette solo di contare nei tuoi totali.',
      archiveConfirmOpen: 'Qui ci sono ancora saldi aperti ({amounts}). Archiviare lo stesso? Non si cancella niente, ma il gruppo smette di contare nei tuoi totali.',
      simplifyOn: 'Il modo più corto per azzerare il gruppo. I debiti vengono girati a terzi, quindi funziona solo se pagate tutti la vostra riga insieme.',
      simplifyOff: 'Chi deve davvero a chi, coppia per coppia. Paghi questo e con quella persona sei a posto.',
      paid: 'ha pagato',
      owesTo: 'deve a',
      youPaid: 'hai pagato',
      paidByN: 'pagata da {n} persone',
      youBorrowed: 'hai preso in prestito',
      evenShare: 'sei in pari',
      notInvolved: 'non coinvolto',
      youOwe: 'devi {name}',
      ownsYou: '{name} ti deve',
      perPerson: 'a persona',
      delete: 'Elimina gruppo',
      deleteConfirm: 'Eliminare "{name}" e tutte le sue spese?',
      edit: 'Modifica gruppo',
      tallyTitle: 'Tally',
      tallyToggle: 'Mostra le mie quote su Tally',
      tallyHint: 'Impostazione personale: ogni spesa aggiunge la tua quota come transazione su Tally (categoria "Even").',
      tallyBackfillConfirm: 'Questo gruppo ha {n} spese precedenti non ancora su Tally. Aggiungerle ora?',
      tallyBackfillOk: 'Aggiungile',
      tallyWipeConfirm: 'Ci sono {n} transazioni già su Tally per questo gruppo. Vuoi rimuovere anche quelle?',
      tallyWipeOk: 'Rimuovile',
      tallyWipeCancel: 'Tienile',
      tallyResync: 'Risincronizza ora',
      tallyResyncConfirm: 'Risincronizzare questo gruppo sul tuo Tally? Le voci mancanti vengono ricreate e gli importi riallineati. Quello che hai modificato a mano su Tally — nome, categoria, importo, data — non viene toccato.',
      tallyResyncDone: '{n} transazioni sincronizzate.',
      currencyView: 'Vista valute',
      viewSeparate: 'Valute separate',
      viewAllIn: 'Tutto in {cur}',
      ratesMissing: 'Alcuni tassi di cambio non sono disponibili — i totali potrebbero essere incompleti.',
    },
    newGroup: {
      title: 'Nuovo gruppo',
      name: 'Nome gruppo',
      namePh: 'es. Viaggio Berlino, Coinquilini',
      emoji: 'Emoji',
      members: 'Membri',
      memberPh: 'Nome',
      addMember: 'Aggiungi membro',
      you: 'Tu',
      create: 'Crea',
      cancel: 'Annulla',
      linked: 'Gunsi',
      unlink: 'Scollega account Gunsi',
      noAccessShort: 'senza accesso',
      linkNoAccess: 'Questo utente non ha ancora accesso a Even — abilitalo dal portal.',
      membersLocked: 'Solo il proprietario del gruppo può modificare i membri.',
      memberHasEntries: "{names} ha già delle spese in questo gruppo, quindi non può essere rimosso. Per collegarlo a un account Gunsi scrivi il suo nome nella sua riga e scegli l'account dalla lista: così lo storico resta suo.",
    },
    expense: {
      title: 'Aggiungi spesa',
      editTitle: 'Modifica spesa',
      description: 'Descrizione',
      descPh: 'es. Cena, Hotel, Spesa',
      amount: 'Importo',
      paidBy: 'Pagato da',
      multiPayer: '+ Più paganti',
      onePayer: 'Un solo pagante',
      splitMode: 'Divisione',
      split: {
        equal: 'In parti uguali',
        exact: 'Importi esatti',
        percent: 'Percentuali',
        shares: 'Quote',
        adjust: 'Con extra personali',
      },
      splitShort: {
        equal: 'Uguale',
        exact: 'Importi',
        percent: 'Percento',
        shares: 'Quote',
        adjust: 'Extra',
      },
      participants: 'Partecipanti',
      date: 'Data',
      time: 'Ora',
      notes: 'Note (opzionali)',
      notesPh: 'Aggiungi una nota…',
      hideTally: 'Non mostrare sul mio Tally',
      deleteConfirm: 'Eliminare questa spesa?',
      save: 'Salva',
      cancel: 'Annulla',
      delete: 'Elimina',
      total: 'Totale',
      remaining: 'Restante',
      amountInvalid: 'Inserisci un importo valido, es. 12,50 o 12.50',
      rateMissing: 'Tasso di cambio per questa data non disponibile — connettiti e riprova',
      adjustHint: '{rest} da dividere in parti uguali · {each} a testa',
      perPerson: 'a persona',
    },
    settle: {
      title: 'Pareggia',
      from: 'Da',
      to: 'A',
      amount: 'Importo',
      date: 'Data',
      notes: 'Note (opzionali)',
      deleteConfirm: 'Eliminare questo pagamento?',
      save: 'Registra pagamento',
      cancel: 'Annulla',
    },
    settings: {
      title: 'Impostazioni',
      appearance: 'Aspetto',
      theme: 'Tema',
      themeSystem: 'Sistema',
      themeLight: 'Chiaro',
      themeDark: 'Scuro',
      style: 'Stile',
      styleModern: 'Moderno',
      styleClassic: 'Classico',
      language: 'Lingua',
      defaultCurrency: 'Valuta predefinita',
      dateFormat: 'Formato data',
      dateFormats: {
        auto: 'Automatico (segue la lingua)',
        dmy: 'GG/MM/AAAA',
        mdy: 'MM/GG/AAAA',
        ymd: 'AAAA-MM-GG',
      },
      data: 'Dati',
      exportJson: 'Esporta JSON',
      importJson: 'Importa JSON',
      reset: 'Cancella tutti i dati',
      resetConfirm: 'Eliminare TUTTI i gruppi, spese e pagamenti? Non è reversibile.',
      invalidJson: 'File JSON non valido.',
      account: 'Account',
    },
    activity: {
      title: 'Attività',
      empty: 'Nulla da mostrare.',
      expense: 'spesa',
      payment: 'pagamento',
      added: 'aggiunta',
      updated: 'modificata',
      paidFor: 'ha pagato',
      nPeople: '{n} persone',
      youLent: 'hai prestato',
      youReceived: 'hai ricevuto',
      youSettled: 'hai saldato',
    },
    common: {
      cancel: 'Annulla',
      save: 'Salva',
      delete: 'Elimina',
      confirm: 'Conferma',
      back: 'Indietro',
      loading: 'Caricamento…',
      error: 'Qualcosa è andato storto.',
      retry: 'Riprova',
      you: 'tu',
      and: 'e',
      currency: 'Valuta',
      searchCurrencyPh: 'Cerca valuta…',
      searchEmojiPh: 'Cerca emoji…',
      ok: 'OK',
      today: 'Oggi',
      now: 'Adesso',
    },
  },
}

export function useEvenI18n() {
  const { locale } = useI18n()
  const lang = (locale || 'en').slice(0, 2)
  return translations[lang] ?? translations.en
}
